<template>
  <!-- lg:pr-4 xl:pr-16  -->
  <StandardTemplate
    id="dashboard"
    class="flex flex-col mb-2 mt-8 lg:ml-10">
    <div class="flex flex-col space-y-5 max-w-[86rem] 4xl:max-w-[113rem]">
      <!-- masthead CTA -->
      <div class="flex flex-col xl:flex-row  xl:justify-between flex-1 px-6 py-7 font-roboto
      bg-white rounded-lg">
        <div class="flex flex-col xl:flex-row items-center text-base font-normal text-neutral xl:mr-8 text-center xl:text-left">
          <span class="text-main-alt font-extrabold mr-2 ">Did you know? </span>
          You can use Talkatoo Dictation in any field, in any program on Windows or Mac.
        </div>
        <div class="flex justify-center">
          <a href="https://talkatoo.com/downloads" target="_blank" class="invisible-anchor">
            <button class="btn-primary font-montserrat text-sm font-extrabold xs:whitespace-nowrap
                          bg-main-dark mt-4 xl:mt-0 py-3 px-8">
              Download the Talkatoo app
            </button>
          </a>
        </div>
      </div>

      <!-- body of the dashboard -->
      <div class="flex flex-col xl:flex-row space-y-5  xl:space-y-0 xl:space-x-5">

        <!-- notes preview -->
        <div :class="['flex flex-col ',
                      userPerms.user_management ? 'xl:basis-[32rem]' : 'basis-[50%]']">
          <div class="block bg-white rounded-lg p-4 lg:p-1 ">
            <!-- heading -->
            <div class="flex-col">
              <div class="pl-4 pt-4 pb-2 text-sm text-neutral font-extrabold">
                Your Recent Notes
              </div>
            </div>
            <div class=" bg-pastel-purple-lighter border-pastel-purple border rounded-md">
              <div class="p-2 overflow-y-auto max-h-[44rem]">
                <BaseLoading v-if="loadingNotes" class="min-h-[20rem]" />
                <template v-else>
                  <div class="hidden last:flex flex-col rounded-md p-3 text-center
                              bg-white border border-rare font-bold">
                    <span class="text-base pb-2">Try out Talkatoo Notes!</span> Record patient notes on your phone and they will be automatically processed as a SOAP note.
                  </div>
                  <div v-for="(day, index) in recentNotes" :key="index"
                       class="flex-col space-y-2">
                    <div class="sticky -top-3 bg-pastel-purple-lighter">

                      <div class="pt-2 pb-1 pl-4 text-lg font-bold text-neutral-alt text-opacity-60">
                        {{ day.heading }}
                      </div>
                    </div>
                    <RouterLink
                      v-for="note in day.notes" :key="note.id"
                      :to="`/notes/${note.id}`" class="flex flex-col font-roboto p-4 invisible-anchor items-baseline
                          bg-white rounded-md border border-rare hover:bg-gray-100 duration-150 transition-colors"
                      @click="trackNoteNav(note)">
                      <span class="text-neutral text-xs">{{ note.displayDate.date }} ({{ note.displayDate.time }})</span>
                      <!-- editors note: holy heck actually working ellipsis overflow? I put way too many thought cycles into these 2 lines of HTML/CSS -->
                      <div class="table table-fixed w-full">
                        <span class="table-cell whitespace-nowrap overflow-ellipsis overflow-hidden">
                          {{ note.preview ? note.preview : note.title ? note.title : 'untitled note' }}
                        </span>
                      </div>
                      <NoteStateTag :note="note" />
                    </RouterLink>
                  </div>
                </template>

              </div>
            </div>
            <div class="flex flex-col flex-1 justify-center p-6">
              <div class="my-auto block pb-2 text-center">
                <Transition name="fade">
                  <div v-if="isUserVerified && noteUsage.notesUsedInPeriod !== undefined" class="flex justify-center fade-duration">
                    <span class="text-sm mr-4 font-normal">
                      <div class="tooltip-basic relative">
                        <strong> {{ noteUsage.notesUsedInPeriod }} </strong> Notes this period <span class="text-sm text-gray-400 material-icons align-baseline">info</span>
                        <div class="tooltip-text inline-block whitespace-nowrap">
                          {{ noteUsage.start }} - {{ noteUsage.end }}
                        </div>
                      </div>
                    </span>
                  </div>
                </Transition>
              </div>
              <div class="flex justify-center">
                <RouterLink to="/notes"
                            class="invisible-anchor btn bg-rare-alt rounded-lg
                                     border-2 border-rare-alt py-3 px-2 xs:px-16
                                   hover:bg-rare hover:border-rare transition-colors">
                  <span class="text-sm text-white font-extrabold">View all notes</span>
                </RouterLink>
              </div>
            </div>
          </div>

        </div>

        <!-- user management section -->
        <div class="flex flex-col 4xl:flex-row flex-1 space-y-5 4xl:space-y-0 4xl:space-x-5
                    2xl:basis-[32rem] 4xl:basis-[40rem] 2xl:min-w-[40rem] 2xl:max-w-[52.75rem] 4xl:max-w-[80rem]">
          <div v-if="userPerms.user_management" id="user-management"
               class="flex flex-col flex-1 4xl:flex-initial p-1 bg-white
                      4xl:h-fit rounded-lg 4xl:min-w-[34rem]">
            <!-- heading -->
            <span class="p-4 text-sm text-neutral font-extrabold">
              User Management
            </span>
            <div class="flex flex-col sm:flex-row flex-1 4xl:flex-initial sm:space-x-1 space-y-1 md:space-y-0">
              <div v-if="subscription && maxDictation > 0" class="flex sm:flex-col sm:flex-1 sm:justify-center bg-pastel-yellow-lighter rounded-md p-5
                          min-w-[10rem] sm:min-h-[15.25rem]">
                <div v-if="!subscription" class="flex flex-1">
                  <BaseLoading class="m-auto" />
                </div>
                <template v-else>
                  <div class="flex sm:flex-1 justify-center sm:pb-4 xs:ml-16 mr-4 xs:mr-8 sm:mx-0 h-12 sm:h-auto">
                    <DoughnutChart
                      percent
                      :chartData="dictationChartData" />
                  </div>
                  <div class="flex sm:flex-grow flex-col items-center font-roboto">
                    <span class="text-neutral text-xs"> Dictation Users</span>
                    <span class="text-black text-3xl font-extrabold"> {{ dictation }} of {{ maxDictation }}</span>
                  </div>
                </template>
              </div>
              <div v-if="subscription && maxVerified > 0"
                   class="flex sm:flex-col sm:flex-1 sm:justify-center bg-pastel-yellow-lighter rounded-md p-5
                          min-w-[10rem] sm:min-h-[15.25rem]">
                <div class="flex sm:flex-1 justify-center sm:pb-4 xs:ml-16 mr-4 xs:mr-8 sm:mx-0 h-12 sm:h-auto">
                  <DoughnutChart
                    percent
                    :chartData="verifiedChartData" />
                </div>
                <div class="flex sm:flex-grow flex-col items-center font-roboto">
                  <span class="text-neutral text-xs"> Verified Users</span>
                  <span class="text-black text-3xl font-extrabold"> {{ verified }} of {{ maxVerified }}</span>
                </div>
              </div>
              <div v-if="subscription" class="flex sm:flex-col sm:flex-1 sm:justify-center bg-pastel-yellow-lighter rounded-md p-5
                          min-w-[10rem] sm:min-h-[15.25rem]">
                <div class="relative flex sm:flex-1 justify-center sm:mb-4 xs:ml-16 mr-4 xs:mr-8 sm:mx-0 h-12 sm:h-auto">
                  <div class="absolute transform -translate-x-1/2 -translate-y-1/2  left-1/2 top-1/2">
                    <img src="@/assets/images/mail_plus.svg" />
                  </div>
                  <!-- placeholder shape just for the circle element -->
                  <DoughnutChart
                    :chartData="{
                      datasets: [{
                        labels: ['', ''],
                        data: [0, 1], //integer list
                        backgroundColor: ['rgba(3, 134, 183, 1)', 'rgba(229, 229, 229, 1)'],
                        borderColor: ['rgba(3, 134, 183, 1)', 'rgba(229, 229, 229, 1)'],
                      }]
                    }"
                  />
                </div>
                <div class="flex sm:flex-1 flex-col items-center font-roboto">
                  <span class="text-neutral text-xs"> Pending Invites </span>
                  <span class="text-black text-3xl font-extrabold">{{ invites }}</span>
                </div>
              </div>
            </div>
            <div class="flex 4xl:flex-initial justify-center p-6">
              <RouterLink to="/user-management" class="invisible-anchor block">
                <button class="btn-secondary py-3 px-2 xs:px-12 text-[13px]">
                  <span class="text-sm font-bold">Add, remove and edit users</span>
                </button>
              </RouterLink>
            </div>
          </div>
          <div class="flex flex-col  4xl:flex-initial 4xl:h-fit p-1 bg-white
                      rounded-lg">
            <!-- heading -->
            <span class="p-4 text-sm text-neutral font-extrabold">
              Success and support
            </span>
            <TipsContainer />
            <div class="flex flex-col flex-wrap 2xl:flex-row space-y-2 2xl:space-y-0 justify-between
                        font-bold text-neutral text-sm
                        px-0 md:px-6 py-6">
              <a href="https://support.talkatoo.com/knowledge" target="_blank"
                 class="invisible-anchor flex items-center no-underline p-2 border
                          border-neutral-lighter bg-gray-100 rounded-md
                          hover:border-neutral hover:bg-gray-200 transition-colors">
                <i class="material-icons no-underline text-main-light text-2xl mr-2">menu_book</i>
                <span></span>Knowledge Base
              </a>
              <a href="https://talkatoo.com/support/" target="_blank"
                 class="invisible-anchor flex items-center no-underline p-2 border
                          border-neutral-lighter bg-gray-100 rounded-md
                          hover:border-neutral hover:bg-gray-200 transition-colors">
                <i class="material-icons no-underline text-main-light text-2xl mr-2">forum</i>
                Contact Support
              </a>
              <a href="https://talkatoo.com/self-guided-onboarding-ai-notes/" target="_blank"
                 class="invisible-anchor flex items-center no-underline p-2 border
                          border-neutral-lighter bg-gray-100 rounded-md
                          hover:border-neutral hover:bg-gray-200 transition-colors">
                <i class="material-icons no-underline text-main-light text-2xl mr-2">school</i>
                Self-Guided Onboarding
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

  </StandardTemplate>
</template>

<script src="./Dashboard.js">
</script>

<style src="./Dashboard.css" scoped></style>